import { useState } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { candidates, clientById, jobById, agentBySlug } from '@/lib/mocks'
import { Header, type Crumb } from './Header'
import { Sidebar } from './Sidebar'

// ── Breadcrumb computed from the current route ──
const SECTION_LABEL: Record<string, string> = {
  dashboard: 'Dashboard',
  pipeline: 'Pipeline',
  candidates: 'Candidates',
  jobs: 'Jobs',
  clients: 'Clients',
  interviews: 'Interviews',
  placements: 'Placements',
  commissions: 'Commissions',
  analytics: 'Analytics',
  settings: 'Settings',
  compliance: 'Compliance',
  audit: 'Audit log',
  inbox: 'Approvals',
  notifications: 'Notifications',
  agents: 'Agents',
}
// Sections that have a real list/index page worth linking back to.
const LINKABLE_SECTIONS = new Set(['candidates', 'jobs', 'clients'])

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function childLabel(section: string, id: string): string {
  if (section === 'candidates') return candidates.find((c) => c.id === id)?.name ?? 'Candidate'
  if (section === 'clients') return clientById(id)?.name ?? 'Client'
  if (section === 'jobs') return id === 'new' ? 'New job' : jobById(id)?.title ?? 'Job'
  if (section === 'agents') {
    const a = agentBySlug(id)
    return a ? `${a.name} agent` : 'Agent'
  }
  return titleCase(id)
}

function buildBreadcrumbs(pathname: string): Crumb[] {
  const segs = pathname.split('/').filter(Boolean)
  if (segs.length === 0 || segs[0] === 'dashboard') return [{ label: 'Dashboard' }]

  const crumbs: Crumb[] = [{ label: 'Dashboard', href: '/dashboard' }]
  const section = segs[0]
  const sectionLabel = SECTION_LABEL[section] ?? titleCase(section)

  if (segs.length === 1) {
    crumbs.push({ label: sectionLabel })
    return crumbs
  }

  crumbs.push(
    LINKABLE_SECTIONS.has(section)
      ? { label: sectionLabel, href: `/${section}` }
      : { label: sectionLabel },
  )
  crumbs.push({ label: childLabel(section, segs[1]) })
  return crumbs
}

interface DashboardLayoutProps {
  /**
   * Page content
   */
  children: React.ReactNode
  /**
   * Current page title
   */
  title?: React.ReactNode
  /**
   * Header action buttons
   */
  headerActions?: React.ReactNode
  /**
   * Navigation sections for sidebar
   */
  navSections?: Array<{
    title?: string
    items: Array<{
      label: string
      href: string
      icon: React.ReactNode
    }>
  }>
  /**
   * Current tenant info (from auth context)
   */
  tenant?: {
    name: string
  }
  /**
   * Current user info (from auth context)
   */
  user?: {
    name: string
    email: string
    avatarUrl?: string
  }
}

// Default navigation icons (inline SVGs to avoid external dependencies)
const HomeIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
    role="presentation"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
    />
  </svg>
)

const FolderIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
    role="presentation"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
    />
  </svg>
)

const UsersIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
    role="presentation"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
)

const SettingsIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    aria-hidden="true"
    role="presentation"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

// Default navigation structure
const defaultNavSections = [
  {
    items: [
      { label: 'Dashboard', href: '/dashboard', icon: <HomeIcon /> },
      { label: 'Projects', href: '/projects', icon: <FolderIcon /> },
    ],
  },
  {
    title: 'WORKSPACE',
    items: [
      { label: 'Team', href: '/team', icon: <UsersIcon /> },
      { label: 'Settings', href: '/settings', icon: <SettingsIcon /> },
    ],
  },
]

/**
 * Dashboard layout with sidebar, header, and content area.
 *
 * Layout structure (from PRD):
 * - Header: 60px fixed height
 * - Sidebar: 240px expanded, 64px collapsed
 * - Content: scrollable main area
 *
 * Multi-tenant aware: displays tenant name and user info.
 *
 * @example
 * <DashboardLayout
 *   title="Dashboard"
 *   tenant={{ name: 'Acme Corp' }}
 *   user={{ name: 'John', email: 'john@acme.com' }}
 * >
 *   <DashboardContent />
 * </DashboardLayout>
 */
export function DashboardLayout({
  children,
  headerActions,
  navSections = defaultNavSections,
  tenant,
  user,
}: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const breadcrumbs = buildBreadcrumbs(pathname)

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar
          sections={navSections}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          tenantName={tenant?.name}
          user={user}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar sections={navSections} tenantName={tenant?.name} user={user} />
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <Header
          breadcrumbs={breadcrumbs}
          actions={headerActions}
          showMobileMenu
          onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <div className="mx-auto w-full max-w-[1280px]">{children}</div>
        </main>
      </div>
    </div>
  )
}
